
import { bookJobGeneral, createUserAddressBookItem, getAddressBookDefault, getProductsGeneral } from '@/api';
import { fetchGetAddressBookAddresses } from '@/helpers/fetchers';
import Button from '@/components/UI/Button.vue';
import LookupAddresses from '@/components/UI/LookupAddresses.vue';
import LookupCustomerCode from '@/components/UI/LookupCustomerCode.vue';
import LookupSuburb from '@/components/UI/LookupSuburb.vue';
import Icon from '@/components/UI/Icon.vue';
import PageTabs from '@/components/UI/PageTabs.vue';
import InputPositiveNumbers from '@/components/UI/InputPositiveNumbers.vue';
import InputIntegers from '@/components/UI/InputIntegers.vue';
import BookingHeader from '@/components/UI/BookingHeader.vue';

import { format, utcToZonedTime } from 'date-fns-tz';
import { timeZoneDefault } from '@/utils/PortalConstants';

import { GeneralBookingFormData, GeneralBookingProductData } from '@/models/generalBooking';
import { PageTabsData, User } from '@/models/main';
import {
  AddressBookEndpointParam,
  AddressBookAirRoadStop,
  AddressBookDefaultData,
  AddressBookResponse,
} from '@/models/addressBook';

import { Component, Vue, Watch } from 'vue-property-decorator';
import { mapState } from 'vuex';
import * as Sentry from '@sentry/vue';
import { notificationErrorText } from '@/utils/PortalConstants';

@Component({
  components: {
    BookingHeader,
    Button,
    Icon,
    InputPositiveNumbers,
    InputIntegers,
    LookupAddresses,
    LookupCustomerCode,
    LookupSuburb,
    PageTabs,
  },
  computed: {
    ...mapState('moduleUser', { user: 'user' }),
    ...mapState(['isSideMenuOpen']),
  },
  data() {
    return {};
  },
})
export default class JobBookingGeneral extends Vue {
  private loading = false;
  private user: User;

  public addPackageDialog = false;
  public addPackageError = '';
  public formSubmitted = false;
  public formError = '';
  public identicalAddresses = false;

  public addressBookData: AddressBookAirRoadStop[] = [];

  public formData: GeneralBookingFormData = {
    customer_code: '',
    caller: '',
    phone: '',
    REF1: '',
    REF2: '',
    REF3: '',
    ref1_prompt: '',
    ref2_prompt: '',
    ref3_prompt: '',
    ref1_required: false,
    ref2_required: false,
    ref3_required: false,
    stops: [
      {
        name: '',
        suburb: '',
        suburbId: -1,
        state: '',
        postcode: '',
        address_line1: '',
        address_line2: '',
        warehouse: false,
        attributes: [],
        number: 1,
        note_text: '',
        note_name: '',
        note_phone: '',
        note_email: '',
        time: {
          earliest: '',
          latest: '',
        },
        minDate: '',
        addToAddressBook: false,
      },
      {
        name: '',
        suburb: '',
        suburbId: -1,
        state: '',
        postcode: '',
        address_line1: '',
        address_line2: '',
        warehouse: false,
        attributes: [],
        number: 2,
        note_text: '',
        note_name: '',
        note_phone: '',
        note_email: '',
        time: {
          earliest: '',
          latest: '',
        },
        minDate: '',
        addToAddressBook: false,
      },
    ],
    products: [],
  };

  public totalData = [];

  public pageTabs: PageTabsData[] = [
    {
      title: '1. Customer',
      icon: 'mdi-file-document-outline',
      link: '1',
      active: true,
    },
    {
      title: '2. Addresses',
      icon: 'mdi-map-marker',
      link: '2',
      active: false,
    },
    {
      title: '3. Packages',
      icon: 'mdi-package-variant-closed',
      link: '3',
      active: false,
    },
  ];

  public productsGeneralList: GeneralBookingProductData[] = [];
  private productsGeneralCustomList: GeneralBookingProductData[] = [];

  public addPackageData = {
    packageBox: '',
    weight: 0,
    quantity: 0,
    description: '',
    customChosen: false,
    type: 'box',
    width: 10,
    height: 10,
    length: 10,
    volume: 0.001,
    boxVolume: 0,
    id: 0,
    name: '',
  };

  public suburbChanged(suburb, stop: number) {
    console.log('surb changed:', suburb, stop, this.formData.stops[stop]);
    this.formData.stops[stop].suburb = suburb.name;
    this.formData.stops[stop].suburbId = suburb.id;
    this.formData.stops[stop].state = suburb.state;
    this.formData.stops[stop].postcode = suburb.postcode;
  }

  @Watch('formData.products', { deep: true })
  private watchProducts() {
    this.totalData = [];

    this.formData.products.map(product => {
      const currentProductIdx = Object.keys(this.totalData).indexOf(product.model);

      if (currentProductIdx === -1) {
        this.totalData[product.model] = {
          quantity: 1,
          volume: product.volume,
          weight: product.weight,
        };
      } else {
        this.totalData[product.model].quantity += 1;
        //
        if (product.volume) {
          const newVolume =
            (Math.ceil(this.totalData[product.model].volume * 1000) + Math.ceil(product.volume * 1000)) / 1000;
          this.totalData[product.model].volume = newVolume;
        }
        const newWeight =
          (Math.ceil(this.totalData[product.model].weight * 1000) + Math.ceil(product.weight * 1000)) / 1000;
        this.totalData[product.model].weight = newWeight;
      }
    });
  }

  // Custom products radio group (modal):
  @Watch('addPackageData.type', { deep: true })
  private watchPackageType() {
    let chosenIdx = -1;
    if (this.addPackageData.type === 'carton') {
      chosenIdx = this.productsGeneralCustomList.findIndex(x => x.name.toUpperCase() === 'Custom Box'.toUpperCase());
    }
    if (this.addPackageData.type === 'pallet') {
      chosenIdx = this.productsGeneralCustomList.findIndex(x => x.name.toUpperCase() === 'Custom Pallet'.toUpperCase());
    }
    if (chosenIdx >= 0) {
      this.addPackageData.id = this.productsGeneralCustomList[chosenIdx].id;
      this.addPackageData.name = this.productsGeneralCustomList[chosenIdx].name;
    }
  }

  public packageBoxSelectChanged(e: string): void {
    const chosenBox = this.productsGeneralList.find(x => x.name === e);
    if (chosenBox) {
      this.addPackageData.boxVolume = (chosenBox.width * chosenBox.height * chosenBox.length) / 1000000;
      this.addPackageData.width = chosenBox.width;
      this.addPackageData.height = chosenBox.height;
      this.addPackageData.length = chosenBox.length;
      this.addPackageData.id = chosenBox.id;
      this.addPackageData.name = chosenBox.name;
    }
  }

  public addPackageWeightChanged(value: string) {
    this.addPackageData.weight = Number(value);
  }

  public addPackageQuantityChanged(value: string): void {
    this.addPackageData.quantity = Number(value);
    this.addPackageError = '';
  }

  public addPackageWidthChanged(value: string): void {
    this.addPackageData.width = Number(value);
    this.recalcPackageVolume();
  }

  public addPackageHeightChanged(value: string): void {
    this.addPackageData.height = Number(value);
    this.recalcPackageVolume();
  }

  public addPackageLengthChanged(value: string): void {
    this.addPackageData.length = Number(value);
    this.recalcPackageVolume();
  }

  private recalcPackageVolume() {
    const newVolume = Number(
      ((this.addPackageData.width * this.addPackageData.height * this.addPackageData.length) / 1000000).toFixed(4),
    );
    this.addPackageData.volume = newVolume;
  }

  //
  private getAddressBookWrapper(): void {
    const params: AddressBookEndpointParam = {
      username: '',
      customer: '',
      warehouse: false,
      page: 1,
    };
    fetchGetAddressBookAddresses(params).then((res: AddressBookResponse) => {
      this.addressBookData = res.results;
    });
  }

  public switchAddresses(): void {
    // TODO: refactor
    const stopFirst = { ...this.formData.stops[0] };
    const stopSecond = { ...this.formData.stops[1] };

    this.formData.stops[0] = { ...stopSecond, number: 1 };
    this.formData.stops[1] = { ...stopFirst, number: 2 };

    const lookup0 = this.$refs['lookupSuburb0'] as HTMLFormElement[];
    lookup0[0].applyValueFromParent({
      name: this.formData.stops[0].suburb,
      id: this.formData.stops[0].suburbId,
      postcode: this.formData.stops[0].postcode,
      state: this.formData.stops[0].state,
    });

    const lookup1 = this.$refs['lookupSuburb1'] as HTMLFormElement[];
    lookup1[0].applyValueFromParent({
      name: this.formData.stops[1].suburb,
      postcode: this.formData.stops[1].postcode,
      id: this.formData.stops[1].suburbId,
      state: this.formData.stops[1].state,
    });

    const lookupAddress0 = this.$refs['lookupAddress0'] as HTMLFormElement[];
    lookupAddress0[0].applyValueFromParent({
      city: null,
      line1: stopSecond.address_line1,
      line2: stopSecond.address_line2,
      line3: null,
      name: stopSecond.name,
      suburb: {
        id: stopSecond.suburbId,
        name: stopSecond.suburb,
        postcode: stopSecond.postcode,
        state: stopSecond.state,
      },
      id: 0,
      warehouse: false,
    });

    const lookupAddress1 = this.$refs['lookupAddress1'] as HTMLFormElement[];
    lookupAddress1[0].applyValueFromParent({
      city: null,
      line1: stopFirst.address_line1,
      line2: stopFirst.address_line2,
      line3: null,
      name: stopFirst.name,
      suburb: {
        id: stopFirst.suburbId,
        name: stopFirst.suburb,
        postcode: stopFirst.postcode,
        state: stopFirst.state,
      },
      id: 0,
      warehouse: false,
    });

    this.$forceUpdate();
  }

  public changeAddressBook(value, idx: number): void {
    if (typeof value === 'string') {
      // Input type
      this.formData.stops[idx].name = value.substring(0, 30);
    } else {
      // List item click
      this.formData.stops[idx].name = value.name.substring(0, 30);
      this.formData.stops[idx].state = value.suburb.state;
      this.formData.stops[idx].address_line1 = value.line1 ? value.line1 : '';
      this.formData.stops[idx].address_line2 = value.line2;
      this.formData.stops[idx].suburb = value.suburb.name;
      this.formData.stops[idx].suburbId = value.suburb.id;
      this.formData.stops[idx].note_name = value.note_name;
      this.formData.stops[idx].note_phone = value.note_phone;
      this.formData.stops[idx].note_text = value.note_text ? value.note_text : '';
      this.formData.stops[idx].note_email = value.note_email || '';
      this.formData.stops[idx].postcode = value.suburb.postcode;
      const lookup = this.$refs[`lookupSuburb${idx}`] as HTMLFormElement[];
      lookup[0].applyValueFromParent(value.suburb);
    }
    // Check identical address names
    this.formData.stops[idx].name !== '' && this.formData.stops[0].name === this.formData.stops[1].name
      ? (this.identicalAddresses = true)
      : (this.identicalAddresses = false);
    this.$forceUpdate();
  }

  public removePackage(idx): void {
    this.formData.products.splice(idx, 1);
  }

  public addProductClicked(): void {
    this.addPackageDialog = true;
  }

  private async bookJob() {
    this.loading = true;

    this.formData.stops.map(async stop => {
      // Save to Address book
      if (stop.addToAddressBook) {
        await createUserAddressBookItem({
          suburb: stop.suburbId,
          name: stop.name,
          line1: stop.address_line1,
          line2: stop.address_line2,
          line3: '',
          city: '',
          note_name: stop.note_name,
          note_phone: stop.note_phone,
          note_email: stop.note_email || '',
          note_text: stop.note_text ? stop.note_text : '',
          warehouse: stop.warehouse,
        })
          .then(res => {
            if (res.status === 201) {
              this.$notify({
                group: 'network',
                text: 'The address was added to the address book',
                type: 'success',
                duration: 3000,
              });
              stop.addToAddressBook = false;
            }
          })
          .catch(() => {
            this.$notify({
              group: 'network',
              title: 'Error creating an address in the address book',
              text: 'You can add it manually on the Address Book page',
              type: 'warn',
              duration: 3000,
            });
          });
      }
      // Remove min date & set latest date to the object
      stop.time.latest = stop.time.earliest;
      delete stop.minDate;
    });

    const data = [
      {
        ...this.formData,
        schema_version: '1.0',
        provider: 'airroad',
      },
    ];

    return bookJobGeneral(data)
      .then(res => {
        return {
          success: true,
          data: res.data,
        };
      })
      .catch(res => {
        // HTTP 400 comes with error message
        if (res?.response?.status === 400 && res?.response?.data.failed.length > 0) {
          Sentry.withScope(scope => {
            scope.setLevel('warning');
            Sentry.captureException(res.response?.data.failed[0].error_reason);
          });
        } else {
          Sentry.withScope(scope => {
            scope.setLevel('error');
            Sentry.captureException(res);
          });
        }
        return {
          success: false,
          data: res,
        };
      })
      .finally(() => {
        this.loading = false;
      });
  }

  public async onSubmitBtnClick(redirect): Promise<void> {
    this.formSubmitted = true;
    const form = this.$refs.form as HTMLFormElement;
    // Check identical address names
    this.formData.stops[0].name !== '' && this.formData.stops[0].name === this.formData.stops[1].name
      ? (this.identicalAddresses = true)
      : (this.identicalAddresses = false);
    // Validate form
    if (form && !this.loading && !this.identicalAddresses) {
      const success = await form.validate();
      if (success && this.formData.products.length > 0) {
        const bookResult = await this.bookJob();
        if (bookResult.success) {
          this.$notify({
            group: 'network',
            text: `Job #${bookResult?.data?.booked[0]?.rh_id} booked successfully`,
            type: 'success',
          });
          if (redirect) {
            // Redirect to Detailed job page
            await this.$router.push({
              path: `/job/${bookResult.data.booked[0].rh_id}/`,
            });
          } else {
            // Reload router to reset the form
            this.$router.go(0);
          }
        } else {
          this.$notify({
            group: 'network',
            title: 'Unknown error during job booking',
            text: notificationErrorText,
            type: 'warn',
          });
        }
      }
    }
  }

  public async onPackageSubmit(): Promise<void> {
    const packageForm = this.$refs.packageForm as HTMLFormElement;
    if (packageForm) {
      const success = await packageForm.validate();
      //
      if (success) {
        if (this.addPackageData.customChosen && this.addPackageData.volume < 0.001) {
          this.$notify({
            group: 'network',
            text: 'The volume should not be more than 0.001 cubic meters',
            type: 'warn',
            duration: 3000,
          });
        } else if (this.formData.products.length + this.addPackageData.quantity <= 32) {
          for (let i = 0; i < this.addPackageData.quantity; i++) {
            this.formData.products.push({
              model: this.addPackageData.customChosen
                ? this.addPackageData.type.toUpperCase()
                : this.addPackageData.packageBox,
              model_id: this.addPackageData.id,
              description: this.addPackageData.description,
              length: this.addPackageData.length,
              width: this.addPackageData.width,
              height: this.addPackageData.height,
              weight: Number(this.addPackageData.weight.toFixed(2)),
              volume: this.addPackageData.customChosen
                ? Math.ceil(this.addPackageData.volume * 1000) / 1000
                : Math.ceil(this.addPackageData.boxVolume * 1000) / 1000,
              from_stop: 1,
              to_stop: 2,
            });
          }
          this.addPackageDialog = false;
          this.addPackageError = '';
        } else {
          this.addPackageError = '32 packages maximum';
        }
      }
    }
  }

  // Customer des data listener
  public customerDataChanged(data): void {
    this.formData.REF1 = data.REF1;
    this.formData.REF2 = data.REF2;
    this.formData.REF3 = data.REF3;
    //
    if (this.$ability.can('staff', 'Job')) {
      this.formData.customer_code = data.customer_code;
      this.formData.caller = data.caller;
      this.formData.phone = data.phone;
    }
  }

  private async getProductsGeneralWrapper() {
    await getProductsGeneral()
      .then(res => {
        this.productsGeneralList = res.filter(x => x.goods_code !== 'CBOX' && x.goods_code !== 'CPAL');
        this.productsGeneralCustomList = res.filter(x => x.goods_code === 'CBOX' || x.goods_code === 'CPAL');
      })
      .catch(() => {
        this.$notify({
          group: 'network',
          title: 'Error getting the products list',
          text: notificationErrorText,
          type: 'warn',
        });
      });
  }

  public async defaultAddressIconClicked(stop_number: number): Promise<void> {
    await getAddressBookDefault(this.formData.customer_code)
      .then((res: AddressBookDefaultData) => {
        const lookupAddress = this.$refs[`lookupAddress${stop_number}`] as HTMLFormElement[];
        if (lookupAddress[0]) {
          lookupAddress[0].applyValueFromParent({ ...res });
        }
        this.formData.stops[stop_number].note_text = res.note_text;
        this.formData.stops[stop_number].note_name = res.note_name;
        this.formData.stops[stop_number].note_phone = res.note_phone;
        this.formData.stops[stop_number].note_email = res.note_email || '';
        this.formData.stops[stop_number].addToAddressBook = false;
        this.changeAddressBook(res, stop_number);
      })
      .catch(() => {
        this.$notify({
          group: 'network',
          title: 'Error getting the default address',
          text: notificationErrorText,
          type: 'warn',
        });
      });
  }

  private mounted() {
    this.getAddressBookWrapper();
    this.getProductsGeneralWrapper();
    const userTimeZone = this.user.timezone || timeZoneDefault;
    this.formData.stops.map(stop => {
      stop.time.earliest = format(utcToZonedTime(new Date(), userTimeZone), 'yyyy-MM-dd');
      stop.time.latest = format(utcToZonedTime(new Date(), userTimeZone), 'yyyy-MM-dd');
      stop.minDate = format(utcToZonedTime(new Date(), userTimeZone), 'yyyy-MM-dd');
    });
  }
}
